import React, { useState } from 'react';
import { fetchOrganizations } from '@/data/api/cas';
import { TCasOrganizationRow } from '@/data/types/cas';
import { Input } from '@/lib/components/ui/input';
import { Building2, Search, Users, ChevronRight, LayoutDashboard, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { InfiniteScroll } from '@/components/common/InfiniteScroll/InfiniteScroll';

export const Organizations: React.FC = () => {
  const [query, setQuery] = useState('');
  const navigate = useNavigate();

  const handleFetchOrganizations = async (params: any) => {
    try {
      const res = await fetchOrganizations({ ...params, query: params.search || params.query });
      if (!res.items) return { payload: [], type: 'api/direct' };
      return { payload: res.items, type: 'api/direct' };
    } catch (err) {
      console.error('Failed to fetch organizations:', err);
      throw err;
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="space-y-8"
    >
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">Organizations</h1>
          <p className="text-gray-500 mt-1 font-medium">Manage and configure customer workspaces</p>
        </div>
        
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input 
            placeholder="Search organizations..." 
            className="pl-10 h-11 bg-white/70 backdrop-blur-sm border-gray-200/60 rounded-xl shadow-sm focus-visible:ring-blue-500"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      </div>

      <InfiniteScroll<TCasOrganizationRow>
        apiAction={handleFetchOrganizations}
        itemsData={[]}
        useDirectResponse={true}
        search={{ searchValue: query, debounceMs: 500, resetOnChange: true }}
        renderEmpty={() => (
          <div className="py-16 text-center bg-white/50 backdrop-blur-md rounded-3xl border border-gray-200/50 border-dashed">
            <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No organizations found</p>
          </div>
        )}
      >
        {(orgs) => (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {orgs.map((org, i) => (
              <motion.div 
                key={org.id} 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.05 }}
                onClick={() => navigate(`/admin/organizations/${org.id}`)}
                className="group relative flex flex-col bg-white/70 backdrop-blur-xl border border-gray-200/60 rounded-3xl p-6 shadow-lg shadow-gray-200/20 hover:shadow-xl hover:shadow-gray-200/40 hover:-translate-y-1 transition-all duration-300 cursor-pointer overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 via-transparent to-purple-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                <div className="relative z-10 flex items-start gap-4 mb-6">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-100/50 to-indigo-100/50 border border-blue-200/50 flex items-center justify-center shrink-0 overflow-hidden shadow-inner group-hover:scale-105 transition-transform duration-300">
                    {org.image_url ? (
                      <img src={org.image_url} alt={org.name} className="w-full h-full object-cover" />
                    ) : (
                      <Building2 className="w-7 h-7 text-blue-500/60" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-gray-900 text-lg truncate group-hover:text-blue-600 transition-colors">{org.name}</h3>
                    <p className="text-sm text-gray-500 truncate font-medium">{org.contact_email || 'No email provided'}</p>
                  </div>
                </div>

                <div className="relative z-10 grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-gray-50/80 rounded-xl p-3 border border-gray-100">
                    <div className="flex items-center gap-1.5 text-gray-500 text-xs font-semibold uppercase tracking-wider mb-1">
                      <Users className="w-3.5 h-3.5" /> Members
                    </div>
                    <div className="font-bold text-gray-900">{org.members}</div>
                  </div>
                  <div className="bg-gray-50/80 rounded-xl p-3 border border-gray-100">
                    <div className="flex items-center gap-1.5 text-gray-500 text-xs font-semibold uppercase tracking-wider mb-1">
                      <LayoutDashboard className="w-3.5 h-3.5" /> Features
                    </div>
                    <div className="font-bold text-gray-900">
                      {org.plan && org.plan.perks ? Object.values(org.plan.perks).filter(Boolean).length : 0} active
                    </div>
                  </div>
                </div>

                <div className="relative z-10 mt-auto flex items-center justify-between text-sm">
                  <div className="flex items-center gap-1.5 text-gray-400">
                    <Calendar className="w-4 h-4" />
                    <span>{new Date(org.created_at).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}</span>
                  </div>
                  <div className="flex items-center gap-1 text-blue-600 font-semibold opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300">
                    Manage <ChevronRight className="w-4 h-4" />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </InfiniteScroll>
    </motion.div>
  );
};
